import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import useSWR from 'swr';
import type { CatalogItem } from '../../@types/catalog-item';
import fetchCatalogItem from '../api/fetch-catalog-item';
import { transformMeToClient } from '../api/user/create-account';
import useCheckoutCompletionRouting from './use-checkout-completion-routing';
import useInsensitiveSearchParams from './use-insensitive-search-params';

interface UseCatalogItemFetchResult {
  cartJurisdictionId?: number;
  catalogItem?: CatalogItem;
  error: Error;
  fetchAttempted: boolean;
  loading: boolean;
  missingCatalogItem: boolean;
}

const useCatalogItemFetch = (): UseCatalogItemFetchResult => {
  const { user } = useUserContext();
  const client = transformMeToClient(user);
  const { enableRoutingEffects } = useCheckoutCompletionRouting();

  const searchParams = useInsensitiveSearchParams();
  const quoteId = searchParams.get('q');
  const catalogItemId = searchParams.get('catalogItemId') || searchParams.get('catalogitemid');

  const canFetch = Boolean(user && (quoteId || catalogItemId));
  const { data, isLoading, error } = useSWR<{ catalogItem: CatalogItem; jurisdictionId: number }>(
    canFetch && { catalogItemId, jurisdictionId: client?.jurisdictionId, quoteId },
    fetchCatalogItem,
    {
      onSuccess: () => {
        if (client?.hasMembership) enableRoutingEffects();
      },
    }
  );

  return {
    cartJurisdictionId: data?.jurisdictionId,
    catalogItem: data?.catalogItem,
    error: error,
    fetchAttempted: Boolean(data),
    loading: isLoading,
    missingCatalogItem: data && !data?.catalogItem,
  };
};

export default useCatalogItemFetch;
